// packages/frontend/src/features/imports/useActiveImports.ts
//
// Ghost cards for the Library (Plan 12, Task 10): in-flight/failed imports and
// photo-import drafts awaiting review, so a fresh import doesn't vanish from
// the library until it lands as a recipe. See GhostRecipeCard.tsx for the
// per-status rendering.
import { useEffect, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import type { ImportJobApiItem, ImportJobKind, ImportJobStatus, RecipeApiItem } from "@recipevault/shared";
import { listImports } from "./api.js";
import { computeRefetchInterval } from "./useImport.js";
import { hostnameOf } from "./shareText.js";
import { db } from "../../sync/db.js";

const TERMINAL: ImportJobStatus[] = ["done", "failed", "over_cap"];

export interface GhostImport {
  jobId: string;
  kind: ImportJobKind;
  status: ImportJobStatus;
  label: string;
  siteName: string | null;
  imageKey: string | null;
  /** Set when this is a `done` photo job whose recipeId matches a Dexie
   *  `status:"pending"` draft — i.e. it's sitting in the review queue. */
  pendingRecipeId: string | null;
}

// ----- Dismissal (module-level, intentionally NOT persisted) -----
//
// A tap-through on a failed/over_cap GhostRecipeCard hides that ghost from the
// library grid for the rest of this app session (the full history stays
// reachable at /imports). Module-level state survives a Library
// remount/navigation but resets on a hard reload — that's the point (see
// Task 10 brief: "dismissed failures shouldn't haunt the grid").
const dismissedFailures = new Set<string>();
const dismissListeners = new Set<() => void>();

export function dismissFailedGhost(jobId: string): void {
  if (dismissedFailures.has(jobId)) return;
  dismissedFailures.add(jobId);
  for (const listen of dismissListeners) listen();
}

function labelFor(job: ImportJobApiItem): string {
  if (job.sourceMeta?.title) return job.sourceMeta.title;
  if (job.kind === "url" && job.url) return hostnameOf(job.url);
  if (job.kind === "text") return "Text import";
  if (job.kind === "photo") return "Photo import";
  return "Import";
}

function toGhost(job: ImportJobApiItem, pendingRecipeId: string | null): GhostImport {
  return {
    jobId: job.id,
    kind: job.kind,
    status: job.status,
    label: labelFor(job),
    siteName: job.sourceMeta?.siteName ?? null,
    imageKey: job.sourceMeta?.imageKey ?? null,
    pendingRecipeId,
  };
}

/**
 * Ghosts for the library grid/list: every non-terminal import job, every
 * failed/over_cap job (until dismissed), and every `done` photo job whose
 * recipeId matches a pending Dexie draft (the mandatory review queue for
 * photo imports — see PHOTO_IMPORT_MODEL in the shared schema).
 *
 * Polls `listImports({})` with the same backoff curve as `useImport`
 * (5s → 10s after 30s unchanged → 30s after 60s unchanged) while any job is
 * non-terminal; stops polling entirely once every job is terminal. The list
 * (and therefore the ghost ordering) comes straight from the API, which
 * already returns jobs newest-first (DynamoDB query on the ULID sort key,
 * ScanIndexForward:false) — no client-side re-sort needed.
 */
export function useActiveImports(): GhostImport[] {
  const [jobs, setJobs] = useState<ImportJobApiItem[]>([]);
  const [, setDismissTick] = useState(0);
  const lastStatusMapRef = useRef<Map<string, ImportJobStatus>>(new Map());
  const lastChangeAtRef = useRef<number>(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-render when a failed/over_cap ghost is dismissed via GhostRecipeCard's
  // tap-through — dismissal itself lives outside React state (module Set).
  useEffect(() => {
    const listen = (): void => setDismissTick((n) => n + 1);
    dismissListeners.add(listen);
    return () => {
      dismissListeners.delete(listen);
    };
  }, []);

  // Self-scheduling poll loop. Deliberately NOT split into "fetch" +
  // "schedule next timer" effects driven by state deps — chaining a setTimeout
  // registration behind a state-update-triggered re-render makes the interval
  // between "data landed" and "next timer armed" depend on React's own
  // scheduler (a MessageChannel task under jsdom), which fake timers don't
  // drive. Scheduling the next poll directly inside the same async function
  // that just resolved keeps the whole loop deterministic under
  // vi.advanceTimersByTimeAsync — the same reason SyncService's CDN-cookie
  // refresh timer (a proven pattern in this codebase) is a plain recursive
  // setTimeout rather than a React effect chain.
  useEffect(() => {
    let cancelled = false;

    async function poll(): Promise<void> {
      try {
        const res = await listImports({});
        if (cancelled) return;
        const nextMap = new Map(res.items.map((j) => [j.id, j.status] as const));
        let changed = nextMap.size !== lastStatusMapRef.current.size;
        if (!changed) {
          for (const [id, status] of nextMap) {
            if (lastStatusMapRef.current.get(id) !== status) {
              changed = true;
              break;
            }
          }
        }
        lastStatusMapRef.current = nextMap;
        if (changed) lastChangeAtRef.current = Date.now();
        setJobs(res.items);

        const anyActive = res.items.some((j) => !TERMINAL.includes(j.status));
        if (!anyActive) return; // nothing left to poll — stop entirely
        const interval = computeRefetchInterval({ status: "queued" }, lastChangeAtRef.current);
        if (interval === null) return;
        timerRef.current = setTimeout(() => {
          void poll();
        }, interval);
      } catch {
        // Best-effort: leave ghosts at their last-known state. No further
        // poll is scheduled on a failed fetch (avoids a tight retry loop);
        // a Library remount starts the loop fresh.
      }
    }

    void poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const pendingDrafts = useLiveQuery(
    async () => db.recipes.filter((r) => r.status === "pending").toArray(),
    [],
    [] as RecipeApiItem[],
  );
  const pendingIds = new Set((pendingDrafts ?? []).map((r) => r.id));

  const ghosts: GhostImport[] = [];
  for (const job of jobs) {
    if (TERMINAL.includes(job.status)) {
      if (job.status === "done") {
        if (job.kind === "photo" && job.recipeId && pendingIds.has(job.recipeId)) {
          ghosts.push(toGhost(job, job.recipeId));
        }
        continue; // done, no pending draft match — not a ghost
      }
      // failed / over_cap
      if (dismissedFailures.has(job.id)) continue;
      ghosts.push(toGhost(job, null));
      continue;
    }
    ghosts.push(toGhost(job, null));
  }
  return ghosts;
}
